import React from 'react';
import styled, { css, keyframes } from 'styled-components';

export type GenerateButtonProps = {
  label?: string;
  hint?: string;
  variantCount?: number;
  maxVariantCount?: number;
  onVariantCountChange?: (next: number) => void;
  onClick?: () => void;
  loading?: boolean;
  disabled?: boolean;
  fullWidth?: boolean;
  className?: string;
};

const pulse = keyframes`
  0%, 100% { opacity: 0.85; }
  50% { opacity: 1; }
`;

const Root = styled.div<{ $fullWidth: boolean }>`
  display: inline-flex;
  align-items: stretch;
  width: ${(p) => (p.$fullWidth ? '100%' : 'auto')};
  border-radius: var(--radius-pill);
  overflow: hidden;
  box-shadow: var(--shadow-sm);
  background: linear-gradient(
    135deg,
    var(--color-ai-gradient-from),
    var(--color-ai-gradient-to)
  );
  border: 1px solid var(--color-border-subtle);
`;

const MainAction = styled.button<{ $loading: boolean }>`
  appearance: none;
  flex: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: var(--space-2);
  padding: 0 var(--space-5);
  height: 44px;
  border: none;
  background: var(--color-accent);
  color: var(--color-text-inverse);
  font-family: var(--font-display);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  letter-spacing: 0.02em;
  cursor: pointer;
  transition:
    background var(--duration-fast) var(--ease-standard),
    transform var(--duration-fast) var(--ease-standard);

  &:hover:not(:disabled) {
    background: var(--color-accent-hover);
  }
  &:active:not(:disabled) {
    background: var(--color-accent-active);
    transform: translateY(1px);
  }
  &:disabled {
    cursor: not-allowed;
    opacity: 0.55;
  }

  ${(p) =>
    p.$loading &&
    css`
      animation: ${pulse} 1.4s ease-in-out infinite;
    `}
`;

const Sparkle = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  font-size: 14px;
`;

const Hint = styled.span`
  font-family: var(--font-sans);
  font-size: 10px;
  font-weight: var(--font-weight-medium);
  letter-spacing: 0.04em;
  opacity: 0.8;
`;

const VariantStepper = styled.div`
  display: inline-flex;
  align-items: center;
  background: var(--color-accent-active);
  color: var(--color-text-inverse);
  border-left: 1px solid rgba(0, 0, 0, 0.12);
`;

const StepperButton = styled.button`
  appearance: none;
  border: none;
  background: transparent;
  color: var(--color-text-inverse);
  width: 32px;
  height: 44px;
  cursor: pointer;
  font-size: 14px;
  display: inline-flex;
  align-items: center;
  justify-content: center;

  &:hover:not(:disabled) {
    background: rgba(255, 255, 255, 0.12);
  }
  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const StepperReadout = styled.div`
  min-width: 44px;
  text-align: center;
  font-family: var(--font-mono);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  letter-spacing: 0.04em;
`;

const StepperLabel = styled.div`
  font-family: var(--font-sans);
  font-size: 9px;
  opacity: 0.7;
  letter-spacing: 0.06em;
  text-transform: uppercase;
`;

export const GenerateButton: React.FC<GenerateButtonProps> = ({
  label = 'Generate',
  hint,
  variantCount,
  maxVariantCount = 4,
  onVariantCountChange,
  onClick,
  loading = false,
  disabled = false,
  fullWidth = true,
  className,
}) => {
  const showStepper = variantCount !== undefined && onVariantCountChange !== undefined;

  return (
    <Root $fullWidth={fullWidth} className={className}>
      <MainAction
        type="button"
        $loading={loading}
        onClick={onClick}
        disabled={disabled || loading}
      >
        <Sparkle aria-hidden="true">✨</Sparkle>
        <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
          <span>{loading ? 'Generating...' : label}</span>
          {hint && <Hint>{hint}</Hint>}
        </span>
      </MainAction>

      {showStepper && (
        <VariantStepper>
          <StepperButton
            type="button"
            aria-label="Decrease variant count"
            disabled={(variantCount ?? 1) <= 1 || loading}
            onClick={() => onVariantCountChange?.(Math.max(1, (variantCount ?? 1) - 1))}
          >
            −
          </StepperButton>
          <div>
            <StepperReadout>{variantCount}×</StepperReadout>
            <StepperLabel>variants</StepperLabel>
          </div>
          <StepperButton
            type="button"
            aria-label="Increase variant count"
            disabled={(variantCount ?? 1) >= maxVariantCount || loading}
            onClick={() => onVariantCountChange?.(Math.min(maxVariantCount, (variantCount ?? 1) + 1))}
          >
            +
          </StepperButton>
        </VariantStepper>
      )}
    </Root>
  );
};

GenerateButton.displayName = 'GenerateButton';
